"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { Home, Star, Heart, ListOrdered, Lock, Menu, X, Trash2, ChevronLeft, ChevronRight, History } from "lucide-react";
import { cn } from "@/lib/helpers/utils";
import { Button } from "@/components/ui/button";
import { UserAuth } from "@/components/UserAuth";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/lib/utils/translate";
import { useFavourites } from "@/contexts/FavouritesContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { hasUserData } from "@/contexts/UserPreferencesContext";
import Swal from "sweetalert2";
import { useLayout } from "@/contexts/LayoutContext";
import { getAppTitle, getCurrentYear } from "@/lib/appTitle";
import { ActivityLogDialog } from "@/components/ActivityLogDialog";
import { loadActivityLogsFromServer } from "@/lib/client-activity-log";
import { useAuth } from "@/contexts/AuthContext";
// import { redrawProgrammeResults, debugHomeResults } from "@/lib/utils/redrawUtils";
import { useEffect, useState as useReactState } from "react";

function redraw2() {
  // Dispatch a custom event to trigger redraw in home page
  window.dispatchEvent(new CustomEvent('redraw-programme-results'));
}

///

function debug2() {
  // Dispatch a custom event to trigger debug in home page
  window.dispatchEvent(new CustomEvent('debug-programme-results'));
}

// Helper component to display current score in sidebar
function CurrentScoreDisplay({ scores }: { scores: Record<string, { current: string; min: string; max: string }> }) {
  const { translate } = useLanguage();
  
  // Generate short level string for a specific score type (min/current/max)
  const getShortLevelString = (scoreType: 'min' | 'current' | 'max'): string => {
    if (!scores || Object.keys(scores).length === 0) return '-';
    
    const CORE_SUBJECTS = [
      "Chinese Language",
      "English Language", 
      "Mathematics",
      "Citizenship and Social Development"
    ];
    
    const parts: string[] = [];
    const processedSubjects = new Set<string>();
    
    // Helper to convert grade to short display form
    const gradeToShort = (grade: string): string => {
      if (!grade || grade === 'N/A') return '-';
      if (grade === 'Attained') return 'A';
      if (grade === 'Unattained') return 'U';
      return grade;
    };
    
    // Add core subjects first (in order)
    for (const subject of CORE_SUBJECTS) {
      if (processedSubjects.has(subject)) continue;
      const score = scores[subject];
      if (score?.[scoreType]) {
        const shortGrade = gradeToShort(score[scoreType]);
        if (shortGrade !== '-') {
          parts.push(shortGrade);
        }
        processedSubjects.add(subject);
      }
    }
    
    // Add elective subjects (any non-core subjects)
    for (const subject of Object.keys(scores)) {
      if (processedSubjects.has(subject)) continue;
      if (CORE_SUBJECTS.includes(subject)) continue;
      const score = scores[subject];
      if (score?.[scoreType]) {
        const shortGrade = gradeToShort(score[scoreType]);
        if (shortGrade !== '-') {
          parts.push(shortGrade);
        }
        processedSubjects.add(subject);
      }
    }
    
    return parts.length > 0 ? parts.join('/') : '-';
  };
  
  const minLevel = getShortLevelString('min');
  const currentLevel = getShortLevelString('current');
  const maxLevel = getShortLevelString('max');
  
  // Don't show if no levels
  if (currentLevel === '-') return null;
  
  // Check if min or max are all N/A (showing as '-')
  const showMin = minLevel !== '-';
  const showMax = maxLevel !== '-';
  
  return (
    <div className="hidden mt-2 px-1">
      <div className="text-xs text-amber-400 mb-1">{translate('yourLevels')}</div>
      <div className="text-xs font-mono text-amber-100/80 space-y-0.5">
        {showMin && <div className="flex justify-between"><span className="text-gray-400">Min:</span> <span>{minLevel}</span></div>}
        <div className="flex justify-between"><span className="text-gray-400">Cur:</span> <span className="text-white font-bold">{currentLevel}</span></div>
        {showMax && <div className="flex justify-between"><span className="text-gray-400">Max:</span> <span>{maxLevel}</span></div>}
      </div>
    </div>
  );
}

export function Sidebar() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [isFunctionsAvailable, setIsFunctionsAvailable] = useReactState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const { user: authUser, status: sessionStatus } = useAuth();
  const { translate, language } = useLanguage();
  const appTitle = getAppTitle(language);
  const currentYear = getCurrentYear();
  const footerTitle = language === "zh" ? appTitle : "JUPAS Analyser";

  const { favourites, clearFavourites } = useFavourites();
  const { preferences, clearPreferences } = useUserPreferences();
  const { isSidebarCollapsed, toggleSidebar } = useLayout();

  // Check if home page functions are available
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const available = typeof (window as any).redrawProgrammeResults === 'function';
      setIsFunctionsAvailable(available);
    }
  }, [pathname]);

  // Retrieve the user's activity logs from the database once signed in
  useEffect(() => {
    if (sessionStatus === "authenticated" && authUser) {
      void loadActivityLogsFromServer();
    }
  }, [sessionStatus, authUser]);

  // Calculate counts
  const homeResultsCount = preferences.homeResultsCount ?? (preferences.homeResults?.length || 0);
  const recommendedCount = preferences.recommendedRecommendations?.length || 0;
  const favouritesCount = favourites.length;
  const prioritiesCount = preferences.prioritiesSelectedProgrammes?.length || 0;

  // Debug logging
  // console.log('Sidebar counts:', { 
  //   homeResultsCount,
  //   homeResultsTotal: preferences.homeResults?.length,
  //   homeResultsEligible: preferences.homeResults?.filter((r: any) => r.isEligible).length,
  //   homeResultsData: preferences.homeResults,
  //   recommendedCount, 
  //   favouritesCount, 
  //   prioritiesCount 
  // });

  // Recommended, Favourites and Priorities are signed-in-only (requirement 3):
  // the links stay visible so the features are discoverable, but a padlock
  // replaces the count and the pages themselves ask for sign-in.
  const isSignedIn = sessionStatus === "authenticated";

  const navigation = [
    { name: translate("home"), href: "/", icon: Home, count: homeResultsCount, gated: false },
    { name: translate("recommended"), href: "/recommended", icon: Star, count: recommendedCount, gated: true },
    { name: translate("favourites"), href: "/favourites", icon: Heart, count: favouritesCount, gated: true },
    { name: translate("priorities"), href: "/priorities", icon: ListOrdered, count: prioritiesCount, gated: true },
  ];

  return (
    <>
      {/* Mobile menu button - top right corner, only visible on mobile */}
      <div className="fixed top-[10px] right-4 z-50 md:hidden">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setIsOpen(!isOpen)}
          className="bg-card shadow-lg border-primary/20 hover:bg-primary hover:text-primary-foreground"
        >
          {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Overlay for mobile - click to close sidebar */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar - collapsed on mobile (<768px), always visible on desktop */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-screen border-r bg-gradient-to-b from-gray-900 via-gray-800 to-stone-900 border-sidebar-border z-40",
          "transition-all duration-300 ease-in-out",
          isSidebarCollapsed ? "w-20" : "w-64",
          "md:translate-x-0", // Always visible on desktop
          isOpen ? "translate-x-0" : "-translate-x-full" // Slide in/out on mobile
        )}
      >
        <div className="flex h-full flex-col relative">
          {/* Logo/Title - Sticky at top */}
          <div className={cn(
            "sticky top-0 z-10 border-b border-sidebar-border space-y-4 bg-gradient-to-b from-gray-900 via-gray-800 to-stone-900",
            isSidebarCollapsed ? "p-4 flex flex-col items-center" : "p-6 pb-0"
          )}>
            <div className={cn("flex items-center gap-3", isSidebarCollapsed && "justify-center")}>
              <Image
                src="/images/icon-1.png"
                alt="JUPAS Analyser Logo"
                width={40}
                height={40}
                className="object-contain"
              />
              {!isSidebarCollapsed && (
                <h1 className="text-lg font-bold text-sidebar-foreground">{appTitle}</h1>
              )}
            </div>
            {/* Language Switcher and Collapse Toggle */}
            {!isSidebarCollapsed && (
              <>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <LanguageSwitcher />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleSidebar}
                    className="hidden md:flex h-9 px-2 gap-1 text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all duration-200"
                    title="Collapse sidebar"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="text-xs"></span>
                  </Button>
                </div>
                {/* Current Score Display */}
                <CurrentScoreDisplay scores={preferences.homeSubjectScores} />
              </>
            )}
          </div>

          {/* Expand Toggle Button (Desktop Only) - Between logo and navigation when collapsed */}
          {isSidebarCollapsed && (
            <div className="hidden md:flex justify-center py-2 border-b border-sidebar-border/50">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSidebar}
                className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all duration-200"
                title="Expand sidebar"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 space-y-1 p-4 overflow-y-auto overflow-x-hidden">
            {navigation.map((item) => {
              const isActive = pathname === item.href;
              const Icon = item.icon;
              const locked = item.gated && !isSignedIn;

              return (
                <Link
                  key={item.name}
                  href={item.href}
                  onClick={() => setIsOpen(false)} // Close sidebar on mobile when clicking link
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-lg shadow-sidebar-primary/30"
                      : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                    isSidebarCollapsed && "justify-center px-2"
                  )}
                  title={
                    locked
                      ? `${item.name} — ${translate("featureLoggedInOnly")}`
                      : isSidebarCollapsed
                        ? item.name
                        : undefined
                  }
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {!isSidebarCollapsed && (
                    <>
                      <span className="flex-1 truncate">{item.name}</span>
                      <span className={cn(
                        "flex items-center gap-1 text-xs",
                        isActive ? "opacity-90" : "opacity-70"
                      )}>
                        {locked ? (
                          <Lock className="h-3.5 w-3.5" aria-label={translate("featureLoggedInOnly")} />
                        ) : (
                          <span className="font-semibold">({item.count})</span>
                        )}
                      </span>
                    </>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Footer with User Information */}
          <div className={cn(
            "border-t border-sidebar-border p-4 space-y-3",
            isSidebarCollapsed && "flex flex-col items-center"
          )}>

            {/* Language Switcher - Show compact version when collapsed */}
            {isSidebarCollapsed && <LanguageSwitcher collapsed={true} />}

            {!isSidebarCollapsed && (
              <>
                {/* Debug Results Button - Only show in development mode when function is available */}
                {
                // process.env.NODE_ENV === 'development' && (
                //   <div className="space-y-2">
                //     <Button
                //       onClick={() => {
                //         debug2();
                //         setIsOpen(false);
                //       }}
                //       variant="ghost"
                //       size="sm"
                //       className="w-full justify-start text-purple-400 hover:text-purple-300 hover:bg-purple-950/20 cursor-pointer"
                //     >
                //       <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                //         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                //       </svg>
                //       {translate('debugResults') || 'Debug Results'}
                //     </Button>
                //   </div>
                // )
                }

                {/* Redraw Results Button - Only show when function is available */}
                {
                // process.env.NODE_ENV === 'development' && (
                //   <div className="space-y-2">
                //     <Button
                //       onClick={() => {
                //         // redrawProgrammeResults()
                //         redraw2();
                //         setIsOpen(false);
                //       }}
                //       variant="ghost"
                //       size="sm"
                //       className="w-full justify-start text-blue-400 hover:text-blue-300 hover:bg-blue-950/20 cursor-pointer"
                //     >
                //       <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                //         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                //       </svg>
                //       {translate('redrawResults') || 'Redraw Results'}
                //     </Button>
                //   </div>
                // )
                }

                {/* Clear Selections Button */}
                <div className="space-y-2">
                  <Button
                    onClick={async () => {
                      // Check if user has any modified data
                      const hasData = hasUserData(preferences, favourites.length);
                      if (!hasData) {
                        // Show toast message that there's nothing to clear
                        await Swal.fire({
                          title: translate('clear-selections'),
                          text: 'No data to clear. All settings are already at default values.',
                          icon: 'info',
                          confirmButtonText: 'OK',
                          timer: 3000,
                          timerProgressBar: true
                        });
                        setIsOpen(false);
                        return;
                      }

                      // Build data details for confirmation
                      const hasSubjects = preferences.homeElectiveSubjects.length > 0;
                      const hasScores = Object.keys(preferences.homeSubjectScores).length > 0;
                      const hasRecommendations = preferences.recommendedRecommendations?.length > 0;
                      const hasFavourites = favourites.length > 0;

                      // Build list of what data will be cleared
                      const dataItems = [];
                      if (hasSubjects) dataItems.push(`• ${preferences.homeElectiveSubjects.length} elective subject(s)`);
                      if (hasScores) dataItems.push(`• Score data for ${Object.keys(preferences.homeSubjectScores).length} subject(s)`);
                      if (hasFavourites) dataItems.push(`• ${favourites.length} favourite programme(s)`);
                      if (hasRecommendations) dataItems.push(`• ${preferences.recommendedRecommendations.length} AI recommendation(s)`);

                      const dataList = dataItems.join('\n');

                      const result = await Swal.fire({
                        title: translate('clear-selections'),
                        html: `<div style="text-align: left;">
                      <p>${translate('clear-selections-confirm')}</p>
                      <br/>
                      <strong>Data to be cleared:</strong><br/>
                      <pre style="font-size: 0.9em; margin-top: 8px;">${dataList}</pre>
                    </div>`,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#dc2626',
                        cancelButtonColor: '#6b7280',
                        confirmButtonText: translate('yes'),
                        cancelButtonText: translate('no')
                      });

                      if (result.isConfirmed) {
                        await Promise.all([clearPreferences(), clearFavourites()]);
                        // The home page keeps the DSE inputs in its own state,
                        // which clearing preferences does not reach. Tell it to
                        // reset those inputs and recalculate from the cleared
                        // state.
                        window.dispatchEvent(new CustomEvent('clear-selections'));
                        setIsOpen(false);
                      }
                    }}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-950/20"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {translate('clear-selections') || 'Clear All Selections'}
                  </Button>
                </div>

                {/* Activity Log Button */}
                <div className="space-y-2">
                  <Button
                    onClick={() => {
                      setShowActivityLog(true);
                      setIsOpen(false);
                    }}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-sky-400 hover:text-sky-300 hover:bg-sky-950/20"
                  >
                    <History className="mr-2 h-4 w-4" />
                    {translate('activityLog')}
                  </Button>
                </div>

              </>
            )}

            {/* User Authentication - Icon only when collapsed */}
            {isSidebarCollapsed ? (
              <div className="flex justify-center">
                <UserAuth minimal={true} />
              </div>
            ) : (
              <UserAuth />
            )}

            {/* Copyright */}
            {!isSidebarCollapsed && (
              <p className="text-xs text-sidebar-foreground/60 text-center">
                © {currentYear} {footerTitle}
              </p>
            )}
          </div>
        </div>
      </aside>

      {/* Activity log viewer */}
      <ActivityLogDialog
        open={showActivityLog}
        onOpenChange={setShowActivityLog}
      />
    </>
  );
}
